import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { statsAPI, tasksAPI, analyticsAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import {
  Calendar,
  Heart,
  Plus,
  Sparkles,
  TrendingUp,
  Loader2,
  ArrowRight,
  Store,
  MapPin,
  Compass,
  CheckCircle2,
  Clock,
  AlertTriangle,
  ListTodo,
  CalendarDays,
  CalendarClock,
  FileCheck,
  Target,
  ExternalLink,
  Mail,
  DollarSign,
  FileText,
  Navigation,
  AlertCircle,
  X,
  BarChart3,
  Zap,
  Send,
  Globe,
  Phone,
  Eye,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';

// ==================== SHARED HELPERS ====================

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatCurrency = (val) => {
  if (val === null || val === undefined) return '—';
  return `$${Number(val).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

const getDaysUntil = (dateStr) => {
  if (!dateStr) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(dateStr + 'T00:00:00');
  return Math.ceil((d - today) / (1000 * 60 * 60 * 24));
};

const STATUS_CONFIG = {
  accepted:  { label: 'Accepted',  bg: 'bg-emerald-100', text: 'text-emerald-700', border: 'border-emerald-400', dot: 'bg-emerald-600', icon: CheckCircle2 },
  submitted: { label: 'Submitted', bg: 'bg-blue-100',    text: 'text-blue-700',    border: 'border-blue-300',    dot: 'bg-blue-600',    icon: Send },
  pending:   { label: 'Pending',   bg: 'bg-purple-100',  text: 'text-purple-700',  border: 'border-purple-300',  dot: 'bg-purple-600',  icon: Clock },
  waitlisted:{ label: 'Waitlisted',bg: 'bg-orange-100',  text: 'text-orange-700',  border: 'border-orange-400',  dot: 'bg-orange-500',  icon: AlertCircle },
  rejected:  { label: 'Rejected',  bg: 'bg-rose-100',    text: 'text-rose-700',    border: 'border-rose-400',    dot: 'bg-rose-500',    icon: X },
  draft:     { label: 'Draft',     bg: 'bg-gray-100',    text: 'text-gray-700',    border: 'border-gray-300',    dot: 'bg-gray-400',    icon: FileText },
};

// ==================== ACTION CENTER TAB ====================

const ActionCenterTab = ({ actionData, stats, upcomingTasks, onCompleteTask }) => {
  const [expandedTable, setExpandedTable] = useState(false);
  const appBreakdown = actionData?.app_breakdown || {};
  const appsDueSoon = actionData?.apps_due_soon || [];
  const upcomingEvents = actionData?.upcoming_events || [];
  const activeApps = actionData?.active_applications || [];
  const totalApps = actionData?.total_applications || 0;

  // Show only first 10 events unless expanded
  const displayEvents = expandedTable ? upcomingEvents : upcomingEvents.slice(0, 10);

  return (
    <div className="space-y-6">
      {/* Application Status Breakdown */}
      <div data-testid="app-status-breakdown">
        <h2 className="text-lg font-bold font-['Outfit'] text-[#1E3A5F] mb-3 flex items-center gap-2">
          <FileCheck className="w-5 h-5" />
          Applications Overview
          <span className="text-sm font-normal text-[#71717A] ml-1">({totalApps} total)</span>
        </h2>
        <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
          {Object.entries(STATUS_CONFIG).map(([status, config]) => {
            const count = appBreakdown[status] || 0;
            const Icon = config.icon;
            return (
              <Link
                key={status}
                to={`/applications?status=${status}`}
                className={`rounded-xl border-2 ${config.border} ${config.bg} p-3 hover:shadow-md transition-all hover:-translate-y-0.5 group`}
                data-testid={`app-status-card-${status}`}
              >
                <div className="flex items-center gap-1.5 mb-1">
                  <Icon className={`w-4 h-4 ${config.text}`} />
                  <span className={`text-xs font-semibold ${config.text} capitalize`}>{config.label}</span>
                </div>
                <p className={`text-2xl font-bold font-['Outfit'] ${config.text}`}>{count}</p>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Applications Due Soon */}
      {appsDueSoon.length > 0 && (
        <div className="rounded-2xl border-2 border-[#FE5F55]/30 bg-[#FE5F55]/5 p-5" data-testid="apps-due-soon">
          <h2 className="text-lg font-bold font-['Outfit'] text-[#FE5F55] mb-3 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            Applications Due Soon
          </h2>
          <div className="space-y-3">
            {appsDueSoon.map((item) => {
              const daysLeft = getDaysUntil(item.deadline);
              return (
                <div
                  key={item.event_id}
                  className="flex items-center gap-3 bg-white rounded-xl p-3 border border-[#FE5F55]/20"
                  data-testid={`due-soon-${item.event_id}`}
                >
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    daysLeft !== null && daysLeft <= 3 ? 'bg-red-100' : daysLeft !== null && daysLeft <= 7 ? 'bg-orange-100' : 'bg-amber-100'
                  }`}>
                    <CalendarClock className={`w-5 h-5 ${
                      daysLeft !== null && daysLeft <= 3 ? 'text-red-600' : daysLeft !== null && daysLeft <= 7 ? 'text-orange-600' : 'text-amber-600'
                    }`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <Link to={`/events/${item.event_id}`} className="font-semibold text-[#1E3A5F] hover:underline line-clamp-1">
                      {item.title}
                    </Link>
                    <p className="text-xs text-[#71717A]">
                      {item.city && item.state ? `${item.city}, ${item.state}` : ''} 
                      {item.date ? ` — ${formatDate(item.date)}` : ''}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {item.booth_cost != null && (
                      <span className="text-xs font-medium text-[#1E3A5F] bg-blue-50 px-2 py-1 rounded-full">
                        {formatCurrency(item.booth_cost)}
                      </span>
                    )}
                    <span className={`text-xs font-bold px-2 py-1 rounded-full ${
                      daysLeft !== null && daysLeft <= 3 ? 'bg-red-100 text-red-700' :
                      daysLeft !== null && daysLeft <= 7 ? 'bg-orange-100 text-orange-700' :
                      'bg-amber-100 text-amber-700'
                    }`}>
                      {daysLeft !== null && daysLeft <= 0 ? 'TODAY' : daysLeft !== null ? `${daysLeft}d left` : 'Deadline'}
                    </span>
                    {item.signup_url && (
                      <a href={item.signup_url} target="_blank" rel="noopener noreferrer" className="p-1.5 hover:bg-[#1E3A5F]/10 rounded-lg transition-colors" title="Apply Now">
                        <ExternalLink className="w-4 h-4 text-[#1E3A5F]" />
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Active Applications Tracker */}
      {activeApps.length > 0 && (
        <div className="rounded-2xl border-2 border-[#E5E5E5] bg-white p-5" data-testid="active-applications">
          <h2 className="text-lg font-bold font-['Outfit'] text-[#1E3A5F] mb-3 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-purple-600" />
            Application Tracker
          </h2>
          <div className="space-y-2">
            {activeApps.map((app) => {
              const cfg = STATUS_CONFIG[app.status] || STATUS_CONFIG.draft;
              const Icon = cfg.icon;
              return (
                <div
                  key={app.app_id}
                  className={`flex items-center gap-3 rounded-xl p-3 border-l-4 ${cfg.border} bg-[#FAFAFA] hover:bg-[#F0F0F0] transition-colors`}
                  data-testid={`active-app-${app.app_id}`}
                >
                  <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold capitalize border ${cfg.bg} ${cfg.text} ${cfg.border}`}>
                    <Icon className="w-3 h-3" />
                    {cfg.label}
                  </div>
                  <Link to={`/events/${app.event_id}`} className="flex-1 min-w-0 font-medium text-[#1E3A5F] hover:underline truncate">
                    {app.title}
                  </Link>
                  <span className="text-xs text-[#71717A] flex-shrink-0">
                    {app.city}{app.state ? `, ${app.state}` : ''}
                  </span>
                  {app.date && (
                    <span className="text-xs text-[#71717A] flex-shrink-0">{formatDate(app.date)}</span>
                  )}
                  {app.booth_cost != null && (
                    <span className="text-xs font-medium text-[#1E3A5F] bg-blue-50 px-2 py-1 rounded-full flex-shrink-0">
                      {formatCurrency(app.booth_cost)}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Upcoming Tasks Widget */}
      {upcomingTasks.length > 0 && (
        <div className="rounded-2xl border-2 border-[#E5E5E5] bg-white p-5" data-testid="upcoming-tasks-widget">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-bold font-['Outfit'] text-[#1E3A5F] flex items-center gap-2">
              <ListTodo className="w-5 h-5" />
              Tasks Due This Week
            </h2>
            <Link to="/applications">
              <Button variant="ghost" size="sm" className="text-[#1E3A5F] font-medium">
                View all <ArrowRight className="w-3 h-3 ml-1" />
              </Button>
            </Link>
          </div>
          <div className="space-y-2">
            {upcomingTasks.slice(0, 5).map((task) => (
              <div
                key={task.id}
                className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${
                  task.days_until_due === 0 ? 'border-red-300 bg-red-50' :
                  task.days_until_due <= 2 ? 'border-amber-300 bg-amber-50' :
                  'border-gray-200 bg-gray-50'
                }`}
                data-testid={`task-${task.id}`}
              >
                <Checkbox checked={false} onCheckedChange={() => onCompleteTask(task.id)} className="h-5 w-5" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-[#1E3A5F] truncate text-sm">{task.title}</p>
                  {task.event_title && (
                    <p className="text-xs text-gray-500 truncate flex items-center gap-1">
                      <Calendar className="w-3 h-3" /> {task.event_title}
                    </p>
                  )}
                </div>
                <span className={`text-xs font-bold px-2 py-1 rounded-full flex-shrink-0 ${
                  task.days_until_due === 0 ? 'bg-red-100 text-red-700' :
                  task.days_until_due <= 2 ? 'bg-amber-100 text-amber-700' :
                  'bg-gray-100 text-gray-700'
                }`}>
                  {task.days_until_due === 0 ? 'Today' : task.days_until_due === 1 ? 'Tomorrow' : `${task.days_until_due}d`}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upcoming Events Quick-Action Table */}
      <div className="rounded-2xl border-2 border-[#E5E5E5] bg-white overflow-hidden" data-testid="upcoming-events-table">
        <div className="p-5 pb-3 flex items-center justify-between">
          <h2 className="text-lg font-bold font-['Outfit'] text-[#1E3A5F] flex items-center gap-2">
            <CalendarDays className="w-5 h-5" />
            Upcoming Events
            <span className="text-sm font-normal text-[#71717A]">({upcomingEvents.length})</span>
          </h2>
          <Link to="/events">
            <Button variant="ghost" size="sm" className="text-[#1E3A5F] font-medium">
              All events <ArrowRight className="w-3 h-3 ml-1" />
            </Button>
          </Link>
        </div>
        
        {upcomingEvents.length === 0 ? (
          <div className="text-center py-10 text-[#71717A]">
            <CalendarDays className="w-10 h-10 mx-auto mb-2 opacity-40" />
            <p className="text-sm">No upcoming events. Start saving events to see them here.</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-t border-b border-[#E5E5E5] bg-[#FAFAFA]">
                    <th className="text-left px-5 py-2.5 font-semibold text-[#4A4A4A] text-xs uppercase tracking-wider">Event</th>
                    <th className="text-left px-3 py-2.5 font-semibold text-[#4A4A4A] text-xs uppercase tracking-wider">Date</th>
                    <th className="text-left px-3 py-2.5 font-semibold text-[#4A4A4A] text-xs uppercase tracking-wider">Deadline</th>
                    <th className="text-right px-3 py-2.5 font-semibold text-[#4A4A4A] text-xs uppercase tracking-wider">Booth Cost</th>
                    <th className="text-center px-3 py-2.5 font-semibold text-[#4A4A4A] text-xs uppercase tracking-wider">Status</th>
                    <th className="text-center px-3 py-2.5 font-semibold text-[#4A4A4A] text-xs uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {displayEvents.map((event) => {
                    const deadlineDays = getDaysUntil(event.deadline);
                    const cfg = event.app_status ? (STATUS_CONFIG[event.app_status] || null) : null;
                    return (
                      <tr key={event.event_id} className="border-b border-[#E5E5E5] hover:bg-[#FAFAFA] transition-colors" data-testid={`event-row-${event.event_id}`}>
                        <td className="px-5 py-3">
                          <Link to={`/events/${event.event_id}`} className="font-semibold text-[#1E3A5F] hover:underline line-clamp-1">
                            {event.title}
                          </Link>
                          <p className="text-xs text-[#71717A]">
                            {event.city}{event.state ? `, ${event.state}` : ''}
                            {event.event_type && <span className="ml-1 capitalize">({event.event_type.replace('_', ' ')})</span>}
                          </p>
                        </td>
                        <td className="px-3 py-3 text-[#4A4A4A] whitespace-nowrap text-xs">{formatDate(event.date)}</td>
                        <td className="px-3 py-3 whitespace-nowrap">
                          {event.deadline ? (
                            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                              deadlineDays !== null && deadlineDays <= 3 ? 'bg-red-100 text-red-700' :
                              deadlineDays !== null && deadlineDays <= 14 ? 'bg-orange-100 text-orange-700' :
                              deadlineDays !== null && deadlineDays <= 30 ? 'bg-amber-100 text-amber-700' :
                              'bg-gray-100 text-gray-600'
                            }`}>
                              {formatDate(event.deadline)}
                            </span>
                          ) : (
                            <span className="text-xs text-[#71717A]">—</span>
                          )}
                        </td>
                        <td className="px-3 py-3 text-right font-medium text-[#1E3A5F] whitespace-nowrap text-xs">
                          {event.booth_cost != null ? formatCurrency(event.booth_cost) : '—'}
                        </td>
                        <td className="px-3 py-3 text-center">
                          {cfg ? (
                            <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full border capitalize ${cfg.bg} ${cfg.text} ${cfg.border}`}>
                              <cfg.icon className="w-3 h-3" />
                              {cfg.label}
                            </span>
                          ) : (
                            <span className="text-xs text-[#71717A]">—</span>
                          )}
                        </td>
                        <td className="px-3 py-3">
                          <div className="flex items-center justify-center gap-1">
                            <Link
                              to={`/events/${event.event_id}`}
                              className="p-1.5 hover:bg-blue-50 rounded-lg transition-colors"
                              title="View Event"
                            >
                              <Eye className="w-4 h-4 text-[#1E3A5F]" />
                            </Link>
                            {(event.website_url || event.signup_url) && (
                              <a
                                href={event.website_url || event.signup_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-1.5 hover:bg-emerald-50 rounded-lg transition-colors"
                                title="Visit Website"
                                data-testid={`website-btn-${event.event_id}`}
                              >
                                <Globe className="w-4 h-4 text-emerald-600" />
                              </a>
                            )}
                            {event.contact_email && (
                              <a
                                href={`mailto:${event.contact_email}`}
                                className="p-1.5 hover:bg-purple-50 rounded-lg transition-colors"
                                title={`Email ${event.contact_name || event.contact_email}`}
                                data-testid={`email-btn-${event.event_id}`}
                              >
                                <Mail className="w-4 h-4 text-purple-600" />
                              </a>
                            )}
                            {event.contact_phone && (
                              <a
                                href={`tel:${event.contact_phone}`}
                                className="p-1.5 hover:bg-amber-50 rounded-lg transition-colors"
                                title={`Call ${event.contact_name || event.contact_phone}`}
                              >
                                <Phone className="w-4 h-4 text-amber-600" />
                              </a>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {upcomingEvents.length > 10 && (
              <div className="p-3 text-center border-t border-[#E5E5E5]">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setExpandedTable(!expandedTable)}
                  className="text-[#1E3A5F] font-medium"
                  data-testid="toggle-events-table"
                >
                  {expandedTable ? (
                    <>Show Less <ChevronUp className="w-3 h-3 ml-1" /></>
                  ) : (
                    <>Show All {upcomingEvents.length} Events <ChevronDown className="w-3 h-3 ml-1" /></>
                  )}
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Quick Actions */}
      <div className="grid md:grid-cols-2 gap-4">
        <Link to="/ai-discover" className="rounded-2xl border-2 border-[#1E3A5F] bg-white p-4 hover:shadow-md hover:-translate-y-0.5 transition-all group flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-[#1E3A5F] flex-shrink-0">
            <Compass className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="font-bold font-['Outfit'] text-[#1E3A5F]">Event Scout</h3>
            <p className="text-sm text-[#4A4A4A]">AI-powered event discovery</p>
          </div>
          <ArrowRight className="w-5 h-5 text-[#1E3A5F] group-hover:translate-x-1 transition-transform flex-shrink-0" />
        </Link>
        <Link to="/add-event" className="rounded-2xl border-2 border-[#E5E5E5] bg-white p-4 hover:shadow-md hover:-translate-y-0.5 transition-all group flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-[#1E3A5F] flex-shrink-0">
            <Plus className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="font-bold font-['Outfit'] text-[#1E3A5F]">Add Event</h3>
            <p className="text-sm text-[#4A4A4A]">Share with the community</p>
          </div>
          <ArrowRight className="w-5 h-5 text-[#1E3A5F] group-hover:translate-x-1 transition-transform flex-shrink-0" />
        </Link>
      </div>
    </div>
  );
};

// ==================== PERFORMANCE TAB ====================

const PerformanceTab = ({ stats, analyticsData }) => {
  const summary = analyticsData?.summary || {};
  const channelBreakdown = analyticsData?.channel_breakdown || [];
  const revenueTrend = analyticsData?.revenue_trend || [];
  const topProducts = analyticsData?.top_products_by_revenue || [];

  const metricCards = [
    { label: 'Total Revenue', value: formatCurrency(summary.total_revenue || 0), icon: DollarSign, color: '#22C55E', sub: `${summary.sales_count || 0} sales` },
    { label: 'Net Profit', value: formatCurrency(summary.net_profit || 0), icon: TrendingUp, color: '#1E3A5F', sub: `${(summary.profit_margin || 0).toFixed(1)}% margin` },
    { label: 'Items Sold', value: summary.total_items_sold || 0, icon: Store, color: '#8B5CF6', sub: 'all time' },
    { label: 'Total Fees', value: formatCurrency(summary.total_fees || 0), icon: AlertTriangle, color: '#FE5F55', sub: 'platform fees' },
  ];

  return (
    <div className="space-y-6">
      {/* Revenue Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="performance-metrics">
        {metricCards.map((m) => (
          <div key={m.label} className="rounded-2xl border-2 border-[#E5E5E5] bg-white p-4 hover:shadow-md transition-shadow">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${m.color}18` }}>
                <m.icon className="w-4 h-4" style={{ color: m.color }} />
              </div>
            </div>
            <p className="text-2xl font-bold font-['Outfit'] text-[#1E3A5F]">{m.value}</p>
            <p className="text-xs text-[#71717A] font-medium">{m.label}</p>
            {m.sub && <p className="text-xs text-[#71717A]/70">{m.sub}</p>}
          </div>
        ))}
      </div>

      {/* Revenue Trend */}
      {revenueTrend.length > 0 && (
        <div className="rounded-2xl border-2 border-[#E5E5E5] bg-white p-5" data-testid="revenue-trend">
          <h2 className="text-lg font-bold font-['Outfit'] text-[#1E3A5F] mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5" />
            Revenue Trend
          </h2>
          <div className="flex items-end gap-1 h-40">
            {revenueTrend.map((m) => {
              const maxRev = Math.max(...revenueTrend.map(r => r.revenue || 0), 1);
              const height = ((m.revenue || 0) / maxRev) * 100;
              const monthLabel = new Date(m.month + '-01T00:00:00').toLocaleDateString('en-US', { month: 'short' });
              return (
                <div key={m.month} className="flex-1 flex flex-col items-center gap-1">
                  <span className="text-xs font-medium text-[#1E3A5F]">{m.revenue > 0 ? formatCurrency(m.revenue) : ''}</span>
                  <div
                    className="w-full rounded-t-lg bg-[#1E3A5F] hover:bg-[#2a4f7a] transition-colors min-h-[4px]"
                    style={{ height: `${Math.max(height, 3)}%` }}
                    title={`${monthLabel}: ${formatCurrency(m.revenue)}`}
                  />
                  <span className="text-xs text-[#71717A]">{monthLabel}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Channel Breakdown */}
      {channelBreakdown.length > 0 && (
        <div className="rounded-2xl border-2 border-[#E5E5E5] bg-white p-5" data-testid="channel-breakdown">
          <h2 className="text-lg font-bold font-['Outfit'] text-[#1E3A5F] mb-4 flex items-center gap-2">
            <Store className="w-5 h-5" />
            Sales Channel Performance
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#E5E5E5]">
                  <th className="text-left py-2 px-3 text-xs font-semibold text-[#4A4A4A] uppercase tracking-wider">Channel</th>
                  <th className="text-right py-2 px-3 text-xs font-semibold text-[#4A4A4A] uppercase tracking-wider">Revenue</th>
                  <th className="text-right py-2 px-3 text-xs font-semibold text-[#4A4A4A] uppercase tracking-wider">Net Profit</th>
                  <th className="text-right py-2 px-3 text-xs font-semibold text-[#4A4A4A] uppercase tracking-wider">Margin</th>
                  <th className="text-right py-2 px-3 text-xs font-semibold text-[#4A4A4A] uppercase tracking-wider">Sales</th>
                </tr>
              </thead>
              <tbody>
                {channelBreakdown.map((ch) => (
                  <tr key={ch.channel} className="border-b border-[#E5E5E5] hover:bg-[#FAFAFA]">
                    <td className="py-2.5 px-3 font-medium text-[#1E3A5F]">{ch.label}</td>
                    <td className="py-2.5 px-3 text-right text-[#4A4A4A]">{formatCurrency(ch.total_revenue)}</td>
                    <td className="py-2.5 px-3 text-right">
                      <span className={ch.net_profit >= 0 ? 'text-emerald-600' : 'text-rose-600'}>
                        {formatCurrency(ch.net_profit)}
                      </span>
                    </td>
                    <td className="py-2.5 px-3 text-right text-[#4A4A4A]">{ch.profit_margin?.toFixed(1)}%</td>
                    <td className="py-2.5 px-3 text-right text-[#4A4A4A]">{ch.sales_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Top Products */}
      {topProducts.length > 0 && (
        <div className="rounded-2xl border-2 border-[#E5E5E5] bg-white p-5" data-testid="top-products">
          <h2 className="text-lg font-bold font-['Outfit'] text-[#1E3A5F] mb-4 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-amber-500" />
            Top Products by Revenue
          </h2>
          <div className="space-y-2">
            {topProducts.slice(0, 5).map((p, i) => {
              const maxRev = topProducts[0]?.total_revenue || 1;
              const barWidth = (p.total_revenue / maxRev) * 100;
              return (
                <div key={p.product_id} className="flex items-center gap-3">
                  <span className="text-xs font-bold text-[#71717A] w-5 text-right">{i + 1}.</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-[#1E3A5F] truncate">{p.product_name}</span>
                      <span className="text-sm font-bold text-[#1E3A5F] flex-shrink-0 ml-2">{formatCurrency(p.total_revenue)}</span>
                    </div>
                    <div className="h-2 bg-[#E5E5E5] rounded-full overflow-hidden">
                      <div className="h-full bg-[#1E3A5F] rounded-full transition-all" style={{ width: `${barWidth}%` }} />
                    </div>
                    <p className="text-xs text-[#71717A] mt-0.5">{p.quantity_sold} sold — {formatCurrency(p.gross_profit)} profit</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Event Planning Timeline */}
      <div className="rounded-2xl border-2 border-[#E5E5E5] bg-white p-5" data-testid="event-timeline">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold font-['Outfit'] text-[#1E3A5F] flex items-center gap-2">
            <Target className="w-5 h-5" />
            Event Planning Timeline
          </h2>
          <Link to="/favorites" className="text-sm text-[#1E3A5F] hover:underline flex items-center gap-1">
            View all <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: 'Next 30 days', value: stats?.next_30_days || 0, color: '#FE5F55' },
            { label: 'Next 60 days', value: stats?.next_60_days || 0, color: '#1E3A5F' },
            { label: 'Next 90 days', value: stats?.next_90_days || 0, color: '#8B5CF6' },
            { label: 'This year', value: stats?.this_year_events || 0, color: '#22C55E' },
          ].map((item) => (
            <div key={item.label} className="text-center p-4 rounded-xl bg-[#FAFAFA] border border-[#E5E5E5]">
              <p className="text-2xl font-bold font-['Outfit']" style={{ color: item.color }}>{item.value}</p>
              <p className="text-xs text-[#4A4A4A] font-medium">{item.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Events by Category */}
      {stats?.events_by_type && Object.keys(stats.events_by_type).length > 0 && (
        <div className="rounded-2xl border-2 border-[#E5E5E5] bg-white p-5" data-testid="events-by-category">
          <h2 className="text-lg font-bold font-['Outfit'] text-[#1E3A5F] mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-[#FE5F55]" />
            Events by Category
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {Object.entries(stats.events_by_type).map(([type, count]) => (
              <div key={type} className="text-center p-3 rounded-xl bg-[#FFEDDF]/60 border border-[#FFEDDF]">
                <Store className="w-5 h-5 mx-auto mb-1 text-[#1E3A5F]" />
                <p className="text-xl font-bold font-['Outfit'] text-[#1E3A5F]">{count}</p>
                <p className="text-xs text-[#71717A] capitalize">{type.replace('_', ' ')}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Link to full Analytics */}
      <Link to="/analytics" className="block rounded-2xl border-2 border-[#1E3A5F] bg-white p-4 hover:shadow-md hover:-translate-y-0.5 transition-all group">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-[#1E3A5F] flex-shrink-0">
            <BarChart3 className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="font-bold font-['Outfit'] text-[#1E3A5F]">Full Analytics Dashboard</h3>
            <p className="text-sm text-[#4A4A4A]">Deep dive into event performance, product analysis, and projections</p>
          </div>
          <ArrowRight className="w-5 h-5 text-[#1E3A5F] group-hover:translate-x-1 transition-transform flex-shrink-0" />
        </div>
      </Link>
    </div>
  );
};

// ==================== MAIN DASHBOARD ====================

export const Dashboard = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('actions');
  const [stats, setStats] = useState(null);
  const [actionData, setActionData] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [upcomingTasks, setUpcomingTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [perfLoading, setPerfLoading] = useState(false);

  useEffect(() => {
    if (authLoading) return; // Wait for auth to finish
    if (!user) { navigate('/login'); return; }

    const fetchData = async () => {
      try {
        const [statsRes, actionRes, tasksRes] = await Promise.all([
          statsAPI.get(),
          statsAPI.getActionCenter(),
          tasksAPI.getUpcoming(7),
        ]);
        setStats(statsRes.data);
        setActionData(actionRes.data);
        setUpcomingTasks(tasksRes.data);
      } catch (error) {
        toast.error('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user, navigate, authLoading]);

  // Lazy-load analytics when Performance tab is first selected
  useEffect(() => {
    if (activeTab === 'performance' && !analyticsData && !perfLoading) {
      setPerfLoading(true);
      analyticsAPI.getDashboard()
        .then(res => setAnalyticsData(res.data))
        .catch(() => toast.error('Failed to load analytics'))
        .finally(() => setPerfLoading(false));
    }
  }, [activeTab, analyticsData, perfLoading]);

  const handleCompleteTask = async (taskId) => {
    try {
      await tasksAPI.update(taskId, { completed: true });
      setUpcomingTasks(prev => prev.filter(t => t.id !== taskId));
      toast.success('Task completed!');
    } catch (error) {
      toast.error('Failed to complete task');
    }
  };

  if (!user || authLoading) return null;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#1E3A5F] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-6 md:px-12" data-testid="dashboard">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-4xl md:text-5xl font-bold font-['Outfit'] tracking-tight text-[#1E3A5F] mb-1">
            Welcome back, {user.name?.split(' ')[0]}!
          </h1>
          <p className="text-base text-[#4A4A4A]">
            Here's what's happening with your vendor journey
          </p>
        </div>

        {/* Tab Switcher */}
        <div className="flex gap-1 mb-6 bg-[#F0F0F0] p-1 rounded-xl w-fit" data-testid="dashboard-tabs">
          <button
            onClick={() => setActiveTab('actions')}
            className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all flex items-center gap-2 ${
              activeTab === 'actions'
                ? 'bg-[#1E3A5F] text-white shadow-md'
                : 'text-[#4A4A4A] hover:text-[#1E3A5F] hover:bg-white/50'
            }`}
            data-testid="tab-actions"
          >
            <Zap className="w-4 h-4" />
            Action Center
          </button>
          <button
            onClick={() => setActiveTab('performance')}
            className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all flex items-center gap-2 ${
              activeTab === 'performance'
                ? 'bg-[#1E3A5F] text-white shadow-md'
                : 'text-[#4A4A4A] hover:text-[#1E3A5F] hover:bg-white/50'
            }`}
            data-testid="tab-performance"
          >
            <BarChart3 className="w-4 h-4" />
            Performance
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === 'actions' ? (
          <ActionCenterTab
            actionData={actionData}
            stats={stats}
            upcomingTasks={upcomingTasks}
            onCompleteTask={handleCompleteTask}
          />
        ) : perfLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-[#1E3A5F] animate-spin" />
          </div>
        ) : (
          <PerformanceTab stats={stats} analyticsData={analyticsData} />
        )}
      </div>
    </div>
  );
};

export default Dashboard;
